import { useMemo, useState, useEffect } from 'react';
import { downloadAsExcel } from '../../../utils/downloadAsExcel';
import styled, { css } from 'styled-components';
import { s } from '../components/PeriodStatsPage.styles';
import InsightSummary from './InsigntSummary';
import { formatCurrency } from '../../../func/Function';
import { useDragScroll } from '../../../utils/useDragScroll';

export type RankItem = {
  id: string;
  caseNo: string;
  address: string;
  usage: string;
  appraisal: number;
  winning: number;
  winRate: number; // 0~1 로 통일
  bidders: number | null; // null 허용
};

type AuctionRankProps = {
  periodSummary: string;
  region: string;
  usage: string;
  usageDetail?: string;
  court?: string;
  type: 'week' | 'month' | 'year';
  selectedType: 'A' | 'B' | 'C' | 'D';
  onTypeChange: (t: 'A' | 'B' | 'C' | 'D') => void;
  data: RankItem[];
  loading?: boolean;
  insights?: string;
  insightsLoading?: boolean;
  insightsError?: string | null;
};

type TabKey = 'A' | 'B' | 'C' | 'D';

const TAB_LABEL: Record<TabKey, string> = {
  A: '낙찰가',
  B: '응찰자 수',
  C: '낙찰가율',
  D: '감정가',
};

type SortKey = 'rank' | 'appraisal' | 'winning' | 'winRate' | 'bidders';
type SortDir = 'asc' | 'desc';

const DEFAULT_SORT_BY_TAB: Record<TabKey, { key: SortKey; dir: SortDir }> = {
  A: { key: 'winning', dir: 'desc' }, // 낙찰가
  B: { key: 'bidders', dir: 'desc' }, // 응찰자 수
  C: { key: 'winRate', dir: 'desc' }, // 낙찰가율
  D: { key: 'appraisal', dir: 'desc' }, // 감정가
};

export default function AuctionRankList({
  periodSummary,
  region,
  usage,
  usageDetail,
  court,
  type,
  selectedType,
  onTypeChange,
  data,
  loading = false,
  insights,
  insightsLoading,
  insightsError,
}: AuctionRankProps) {
  const [tab, setTab] = useState<TabKey>(selectedType);
  useEffect(() => {
    setTab(selectedType);
    setSortKey(DEFAULT_SORT_BY_TAB[selectedType].key);
    setSortDir(DEFAULT_SORT_BY_TAB[selectedType].dir);
    setShowAll(false);
  }, [selectedType]);

  const [sortKey, setSortKey] = useState<SortKey>(
    DEFAULT_SORT_BY_TAB[selectedType].key,
  );
  const [sortDir, setSortDir] = useState<SortDir>(
    DEFAULT_SORT_BY_TAB[selectedType].dir,
  );
  const [showAll, setShowAll] = useState(false);

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const onChangeTab = (t: TabKey) => {
    setTab(t);
    setSortKey(DEFAULT_SORT_BY_TAB[t].key);
    setSortDir(DEFAULT_SORT_BY_TAB[t].dir);
    setShowAll(false);
    onTypeChange(t);
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const num = (v: unknown, fallback = 0) =>
    typeof v === 'number' && !isNaN(v) ? v : fallback;

  const norm = (s: unknown) => String(s ?? '').trim();

  const makeStableId = (r: RankItem) => {
    // 사건번호 + 주소 + 금액/응찰자 등을 섞어 충돌 방지
    return [
      norm(r.id), // 기존 id(보통 caseNo 기반)
      norm(r.caseNo),
      norm(r.address),
      num(r.winning, -1),
      num(r.appraisal, -1),
      num(r.bidders, -1),
    ].join('::');
  };

  const prepared = useMemo(() => {
    const base = Array.isArray(data) ? data : [];
    return base.map((r) => ({
      ...r,
      id: makeStableId(r),
      caseNo: norm(r.caseNo),
      address: norm(r.address),
      usage: norm(r.usage),
    }));
  }, [data]);

  const sorted = useMemo(() => {
    const base = [...prepared];

    // 1차: 탭 기본 기준 정렬
    const primary = DEFAULT_SORT_BY_TAB[tab];
    const pick = (row: RankItem, key: SortKey) =>
      key === 'appraisal'
        ? num(row.appraisal)
        : key === 'winning'
          ? num(row.winning)
          : key === 'winRate'
            ? num(row.winRate)
            : key === 'bidders'
              ? num(row.bidders, 0)
              : 0;

    base.sort((a, b) => {
      const va = pick(a, primary.key);
      const vb = pick(b, primary.key);
      const diff = va - vb;
      if (diff !== 0) return primary.dir === 'asc' ? diff : -diff;
      // 동률 보정: 사건번호/주소로 안정 타이브레이커
      const tie =
        a.caseNo.localeCompare(b.caseNo) || a.address.localeCompare(b.address);
      return tie;
    });

    // 순위 부여 (탭 기준)
    const withRank = base.map((row, idx) => ({ ...row, rank: idx + 1 }));

    // 2차: 사용자 클릭 정렬
    const final = [...withRank].sort((a: any, b: any) => {
      const va = num(a[sortKey], 0);
      const vb = num(b[sortKey], 0);
      const diff = va - vb;
      if (diff !== 0) return sortDir === 'asc' ? diff : -diff;
      // 동률 보정
      return (
        a.caseNo.localeCompare(b.caseNo) || a.address.localeCompare(b.address)
      );
    });

    return final;
  }, [prepared, tab, sortKey, sortDir]);

  // 엑셀로 내릴 라벨(탭명)
  const tabTitle = TAB_LABEL;

  const handleDownloadExcel = () => {
    // 화면에 보이는 순서/개수와 동일하게 (Top10 또는 전체)
    const visible = sorted.slice(0, showAll ? sorted.length : 10);

    // 엑셀 행 구성 (가독성 위해 포맷팅된 문자열로 저장)
    const rows = visible.map((r) => ({
      순위: r.rank,
      사건번호: r.caseNo,
      소재지: r.address,
      용도: r.usage,
      감정가: r.appraisal.toLocaleString('ko-KR'),
      낙찰가: r.winning.toLocaleString('ko-KR'),
      '낙찰가율(%)': (r.winRate * 100).toFixed(1),
      응찰자수: r.bidders ?? '-',
    }));

    const filename = `주요_경매_물건_${tabTitle[tab]}`;
    downloadAsExcel(rows as any[], filename);
  };

  const insightContent = useMemo(() => {
    if (insightsLoading) return '인사이트를 불러오는 중…';
    if (insightsError) return insightsError;
    const t = (insights ?? '').trim();
    return t.length ? t : '표시할 인사이트가 없어요.';
  }, [insights, insightsLoading, insightsError]);

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img>
            <img src="/assets/images/icon/trophy.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>주요 경매 물건 순위</h3>
          <p>해당 기간 낙찰가·응찰자수·낙찰가율·감정가 기준 상위 경매 물건</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>
      <TabRow>
        {(Object.keys(TAB_LABEL) as TabKey[]).map((k) => (
          <TabPill key={k} $active={tab === k} onClick={() => onChangeTab(k)}>
            {TAB_LABEL[k]}
          </TabPill>
        ))}
      </TabRow>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="목록을 불러오는 중"
          />
        </LoaderWrap>
      ) : (
        <>
          <InsightSummary
            title="인사이트 요약"
            category={TAB_LABEL[tab]}
            content={insightContent}
          />

          <s.TableWrap>
            <TableContainer
              ref={ref}
              onMouseDown={onMouseDown}
              onMouseLeave={onMouseLeave}
              onMouseUp={onMouseUp}
              onMouseMove={onMouseMove}
            >
              <s.Table>
                <thead>
                  <tr>
                    <Th width="60px" align="center">
                      <SortHeader
                        $align="center"
                        onClick={() => toggleSort('rank')}
                      >
                        <Arrow $active={sortKey === 'rank'} $dir={sortDir} />{' '}
                        순위
                      </SortHeader>
                    </Th>
                    <Th width="242px">사건번호 / 소재지</Th>
                    <Th width="90px" align="center">
                      용도
                    </Th>
                    <Th width="120px">
                      <SortHeader onClick={() => toggleSort('appraisal')}>
                        <Arrow
                          $active={sortKey === 'appraisal'}
                          $dir={sortDir}
                        />{' '}
                        감정가
                      </SortHeader>
                    </Th>
                    <Th width="120px">
                      <SortHeader onClick={() => toggleSort('winning')}>
                        <Arrow $active={sortKey === 'winning'} $dir={sortDir} />{' '}
                        낙찰가
                      </SortHeader>
                    </Th>
                    <Th width="108px" align="right">
                      <SortHeader onClick={() => toggleSort('winRate')}>
                        <Arrow $active={sortKey === 'winRate'} $dir={sortDir} />{' '}
                        낙찰가율
                      </SortHeader>
                    </Th>
                    <Th width="108px" align="center">
                      <SortHeader onClick={() => toggleSort('bidders')}>
                        <Arrow $active={sortKey === 'bidders'} $dir={sortDir} />{' '}
                        응찰자 수
                      </SortHeader>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.slice(0, showAll ? sorted.length : 10).map((row) => (
                    <tr key={row.id}>
                      <Td align="center">{row.rank}</Td>
                      <Td>
                        <CaseBox>
                          {row.caseNo}
                          <Addr title={row.address}>{row.address}</Addr>
                        </CaseBox>
                      </Td>
                      <Td align="center">{row.usage}</Td>
                      <Td align="right">{formatCurrency(row.appraisal)}</Td>
                      <Td align="right">{formatCurrency(row.winning)}</Td>
                      <Td align="right">{(row.winRate * 100).toFixed(1)}%</Td>
                      <Td align="right">{row.bidders ?? '업데이트 중'}</Td>
                    </tr>
                  ))}
                </tbody>
              </s.Table>
            </TableContainer>
          </s.TableWrap>

          {sorted.length > 10 && (
            <s.MoreButton onClick={() => setShowAll((v) => !v)}>
              <span>{showAll ? '접기' : '더보기'}</span>
              <img
                src="/assets/images/arrow/arrow-down-gray.svg"
                alt=""
                style={{
                  transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </s.MoreButton>
          )}
        </>
      )}
    </s.Wrap>
  );
}

const TabRow = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  background: #f4f5f7;
  border-radius: 8px;
  padding: 2px;
`;
const TabPill = styled.button<{
  $active?: boolean;
}>`
  padding: 8px 12px;
  background: transparent;
  flex: 1 0 0;
  border: none;
  color: #a7adb8;
  font-size: 14px;
  cursor: pointer;
  border-radius: 6px;
  ${({ $active }) =>
    $active &&
    css`
      background: #fff;
      color: #0c0e12;
      font-weight: 500;
      box-shadow:
        0px 4px 16px 0px rgba(39, 44, 54, 0.08),
        0px 1px 2px 0px rgba(39, 44, 54, 0.12),
        0px 2px 6px 0px rgba(39, 44, 54, 0.05);
    `}
`;

const Th = styled.th<{ width?: string; align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  width: ${(p) => p.width ?? 'auto'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  vertical-align: top;
`;

const SortHeader = styled.button<{ $align?: 'left' | 'center' | 'right' }>`
  border: none;
  outline: none;
  background-color: transparent;

  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  cursor: pointer;

  justify-content: ${({ $align }) =>
    $align === 'right'
      ? 'flex-end'
      : $align === 'center'
        ? 'center'
        : 'flex-end'};
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' // 활성 + 내림
            : '/assets/images/arrow/red_arrow_up.svg', // 활성 + 오름
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' };
  },
)`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const CaseBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Addr = styled.span`
  color: #787d87;
  font-weight: 500;
  font-size: 13px;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
